import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleData } from '@/mocks/articles';

export default function ArticleDynamicPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articleData.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-[72px] min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-600 mb-6">O artigo que você procura não está disponível.</p>
            <Link to="/" className="inline-flex items-center px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap">
              Voltar ao Início
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <title>{article.metaTitle}</title>
      <meta name="description" content={article.metaDescription} />
      <meta name="keywords" content={article.metaKeywords} />
      <link rel="canonical" href={`https://cssbuyspreadsheet.biz/artigo/${article.slug}/`} />

      <Navbar />

      <main>
        {/* Article Header */}
        <section className="relative pt-[72px]">
          <div className="relative h-[40vh] min-h-[320px] mx-4 md:mx-6 mt-4 md:mt-6 rounded-2xl md:rounded-3xl overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.metaTitle}
              title={article.metaTitle}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/30"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white max-w-[800px] leading-tight">
                {article.metaTitle}
              </h1>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-12 md:py-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-[800px] mx-auto">
            {/* Tag */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent-200/60 bg-accent-50 mb-8">
              <span className="text-xs font-medium text-accent-700 tracking-wide">/ {article.tag}</span>
            </div>

            {/* Content sections */}
            {article.sections.map((section, sIdx) => (
              <div key={sIdx}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 mt-10 first:mt-0">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5"
                    dangerouslySetInnerHTML={{ __html: p }}
                  />
                ))}
              </div>
            ))}

            {/* Back link */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-2.5 text-sm font-medium bg-foreground-950 text-background-50 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Voltar à Página Inicial
              </Link>
            </div>

            {/* Mini CTA */}
            <div className="relative rounded-2xl overflow-hidden p-8 md:p-10 text-center mt-10">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20product%20flat%20lay%20with%20diverse%20fashion%20accessories%20scattered%20elegantly%20on%20cream%20surface%20soft%20natural%20lighting%20clean%20editorial%20photography%20beige%20palette%20harmonious%20composition%20no%20text%20no%20logos%20subtle%20artistic%20shadows&width=1000&height=400&seq=article-cta-dynamic-v2-01&orientation=landscape"
                  alt="CSSBuy Spreadsheet CTA"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/55"></div>
              </div>
              <div className="relative z-10">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                  Pronto para Começar?
                </h3>
                <p className="text-sm text-white/80 mb-6 max-w-[400px] mx-auto">
                  Acesse agora nossa CSSBuy Spreadsheet completa e descubra os melhores produtos.
                </p>
                <a
                  href={article.ctaLink}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-7 py-3 text-sm font-semibold bg-black/80 text-white rounded-full hover:bg-black transition-all whitespace-nowrap"
                >
                  {article.ctaText}
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'CSSBuySpreadsheet', item: 'https://cssbuyspreadsheet.biz/' },
                { '@type': 'ListItem', position: 2, name: article.metaTitle, item: `https://cssbuyspreadsheet.biz/artigo/${article.slug}/` },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              '@id': `https://cssbuyspreadsheet.biz/artigo/${article.slug}/#article`,
              headline: article.metaTitle,
              description: article.metaDescription,
              image: article.heroImage,
              author: { '@type': 'Organization', name: 'CSSBuySpreadsheet' },
              publisher: { '@type': 'Organization', name: 'CSSBuySpreadsheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png' } },
              datePublished: '2026-06-27',
              dateModified: '2026-06-27',
              inLanguage: 'pt',
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://cssbuyspreadsheet.biz/artigo/${article.slug}/`,
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}